/**
 * LoyaltyLion simple components — essence Royal Rewards (OCMSUPPORT-8285).
 *
 * The markup lives in snippets/loyaltylion-points.liquid. The SDK is loaded by the
 * LoyaltyLion app, so everything here is defensive about it not being there yet.
 *
 * Two things about the SDK drive this design, both confirmed against production:
 *
 *   1. ui.refresh() BLANKS every data-lion-* span and refills it asynchronously,
 *      1-8s later. So a refresh is itself a visible flicker, and we must not call it
 *      unless something actually needs filling.
 *   2. It APPENDS its value to that span rather than replacing the contents, so
 *      anything we write in there is concatenated with the SDK's number ("105105").
 *      Its spans are therefore read-only to us.
 *   3. Dawn re-renders whole sections (and shade-ui.js replaces the entire
 *      ProductInfo innerHTML), which hands us fresh, empty spans.
 *
 * So: the last answer for a given question is cached, and while an identical span is
 * empty we show that answer from a sibling element of our own — a shade change keeps
 * the same price, hence the same points, hence no flicker — swapping back to the SDK's
 * own span the moment it has a number. The SDK is only asked when nothing can be shown.
 *
 * Depends only on pubsub.js / constants.js (bare globals, hence the typeof guards).
 */
(() => {
  const WRAPPER_SELECTOR = '[data-ll-points]';
  const VALUE_SELECTOR = [
    '[data-lion-points]',
    '[data-lion-points-for]',
    '[data-lion-points-for-product-id]',
    '[data-lion-points-for-rule]',
  ].join(', ');
  const PRICE_SELECTOR = '[data-lion-price-for-product-id]';
  const READY_CLASS = 'll-points--ready';
  const FALLBACK_CLASS = 'll-points__fallback';

  const REFRESH_DEBOUNCE = 120;
  const RETRY_DELAYS = [1000, 2500, 5000, 9000];
  const SDK_POLL_INTERVAL = 300;
  const SDK_POLL_LIMIT = 40; // give the app ~12s to load

  const canRefresh = () =>
    Boolean(window.loyaltylion && window.loyaltylion.ui && typeof window.loyaltylion.ui.refresh === 'function');

  /* ------------------------------------------------------- answers we've seen */

  // key -> last value the SDK rendered for it. Per page load, never persisted: a
  // cached answer is only ever reused for an identical question.
  const answers = new Map();

  function keyFor(element) {
    if (element.hasAttribute('data-lion-points-for-product-id')) {
      // Points depend on the price, so the price is part of the question.
      const id = element.getAttribute('data-lion-points-for-product-id');
      const price = document.querySelector(`[data-lion-price-for-product-id="${CSS.escape(id)}"]`);
      return `product:${id}:${price ? price.textContent.trim() : ''}`;
    }
    if (element.hasAttribute('data-lion-points-for')) {
      return `total:${element.getAttribute('data-lion-points-for')}`;
    }
    if (element.hasAttribute('data-lion-points-for-rule')) {
      return `rule:${element.getAttribute('data-lion-points-for-rule')}`;
    }
    if (element.hasAttribute('data-lion-points')) {
      return `balance:${element.getAttribute('data-lion-points')}`;
    }
    return null;
  }

  /* ---------------------------------------------------------------- reveal */

  // The SDK fills (and blanks) the spans whenever it feels like it, so the wrappers
  // watch themselves rather than guessing at a delay.
  const readyObserver = new MutationObserver(() => syncReadyState());

  // Our stand-in for one SDK span, as its next sibling. Created here rather than in
  // Liquid so it survives every section re-render without the snippet knowing.
  function fallbackFor(value) {
    let node = value.nextElementSibling;

    if (!node || !node.classList.contains(FALLBACK_CLASS)) {
      node = document.createElement('span');
      node.className = FALLBACK_CLASS;
      node.hidden = true;
      value.after(node);
    }

    return node;
  }

  // Writes are guarded: assigning an identical value would still fire the observer
  // that called us, and we would never stop.
  function render(node, text, hidden) {
    if (node.textContent !== text) node.textContent = text;
    if (node.hidden !== hidden) node.hidden = hidden;
  }

  // What the shopper actually sees for this span: the SDK's number, or ours.
  function shownValue(value) {
    const own = value.textContent.trim();
    if (own !== '') return own;

    const fallback = value.nextElementSibling;

    return fallback && fallback.classList.contains(FALLBACK_CLASS) && !fallback.hidden
      ? fallback.textContent.trim()
      : '';
  }

  // Returns true when at least one wrapper is still waiting for a number.
  function syncReadyState() {
    let pending = false;

    document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      const values = Array.from(wrapper.querySelectorAll(VALUE_SELECTOR));
      if (values.length === 0) return;

      let isPopulated = true;

      values.forEach((value) => {
        const key = keyFor(value);
        const own = value.textContent.trim();
        const fallback = fallbackFor(value);

        if (own !== '') {
          // The SDK has answered: remember it and get out of its way.
          if (key) answers.set(key, own);
          render(fallback, '', true);
        } else if (key && answers.has(key)) {
          render(fallback, answers.get(key), false);
        } else {
          render(fallback, '', true);
          isPopulated = false;
        }
      });

      wrapper.classList.toggle(READY_CLASS, isPopulated);

      if (isPopulated) {
        applyLabel(wrapper, values[0]);
      } else {
        pending = true;
      }
    });

    return pending;
  }

  // Copy is kept in Liquid: data-ll-points-label="You have [points] Royal Rewards points".
  function applyLabel(wrapper, valueElement) {
    const template = wrapper.getAttribute('data-ll-points-label');
    if (!template) return;

    wrapper.setAttribute('aria-label', template.replace('[points]', shownValue(valueElement)));
  }

  function watchWrappers() {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      readyObserver.observe(wrapper, { childList: true, characterData: true, subtree: true });
    });
  }

  /* --------------------------------------------------------------- refresh */

  let refreshTimer = null;
  let retryTimer = null;
  let retryIndex = 0;
  let sdkTimer = null;
  let sdkPolls = 0;

  // Entry point for "the DOM changed". Resets the retry budget, since this is a new
  // situation rather than a continuation of one we're already chasing.
  function refresh() {
    stopRetries();

    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(runRefresh, REFRESH_DEBOUNCE);
  }

  function runRefresh() {
    refreshTimer = null;

    watchWrappers();

    // Nothing is waiting on a number, so asking the SDK would only blank what is
    // already correct. Unrelated apps mutate the cart DOM constantly; this is what
    // keeps their churn from flickering our lines.
    if (!syncReadyState()) {
      stopRetries();
      return;
    }

    if (!canRefresh()) {
      waitForSdk();
      return;
    }

    try {
      window.loyaltylion.ui.refresh();
    } catch (error) {
      // A failed refresh must never take the page down with it.
    }

    scheduleRetry();
  }

  // The SDK can take several seconds, and a single refresh does not always land, so
  // keep poking it on a bounded, widening schedule until every span has a number.
  function scheduleRetry() {
    if (retryTimer || retryIndex >= RETRY_DELAYS.length) return;

    const delay = RETRY_DELAYS[retryIndex];
    retryIndex += 1;

    retryTimer = setTimeout(() => {
      retryTimer = null;

      if (!syncReadyState()) {
        retryIndex = 0;
        return;
      }

      runRefresh();
    }, delay);
  }

  function stopRetries() {
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
    retryIndex = 0;
  }

  function waitForSdk() {
    if (sdkTimer) return;

    sdkTimer = setInterval(() => {
      sdkPolls += 1;

      if (canRefresh()) {
        clearInterval(sdkTimer);
        sdkTimer = null;
        runRefresh();
        return;
      }

      if (sdkPolls >= SDK_POLL_LIMIT) {
        clearInterval(sdkTimer);
        sdkTimer = null;
      }
    }, SDK_POLL_INTERVAL);
  }

  /* ------------------------------------------------------------- DOM swaps */

  // Mutations the SDK (or this file) caused are ignored, otherwise refreshing the
  // spans would trigger another refresh.
  function isSelfMutation(mutation) {
    const target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
    if (!target || !target.closest) return false;

    return Boolean(target.closest(WRAPPER_SELECTOR) || target.closest(PRICE_SELECTOR));
  }

  const domObserver = new MutationObserver((mutations) => {
    if (mutations.every(isSelfMutation)) return;

    refresh();
  });

  function observeRegion(element) {
    if (!element) return;

    domObserver.observe(element, { childList: true, subtree: true });
  }

  function observeRegions() {
    // Cart drawer: cart-drawer.js swaps .drawer__inner, cart.js replaces
    // .cart-drawer__footer. Cart page: cart.js swaps the .js-contents blocks.
    observeRegion(document.querySelector('cart-drawer'));
    observeRegion(document.getElementById('main-cart-footer'));
    observeRegion(document.getElementById('main-cart-items'));

    // PDP: shade-ui.js replaces the whole ProductInfo innerHTML after its own fetch.
    document.querySelectorAll('[id^="ProductInfo-"]').forEach(observeRegion);
  }

  /* --------------------------------------------------------------- pub/sub */

  function formatPrice(cents) {
    return (Math.round(cents) / 100).toFixed(2);
  }

  function subscribeToTheme() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const data = event && event.data;
      const variant = data && data.variant;
      if (!variant || typeof variant.price !== 'number') return;

      const scope = data.sectionId ? document.getElementById(`ProductInfo-${data.sectionId}`) : null;

      (scope || document).querySelectorAll(PRICE_SELECTOR).forEach((element) => {
        element.textContent = formatPrice(variant.price);
      });

      refresh();
    });

    // product-form.js publishes cartUpdate *before* it renders the drawer, so this is
    // only a backstop — the observers above are what catch the actual swap.
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      requestAnimationFrame(() => setTimeout(refresh, 0));
    });
  }

  /* ------------------------------------------------------------------ init */

  function init() {
    watchWrappers();
    observeRegions();
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  subscribeToTheme();

  document.addEventListener('shopify:section:load', () => {
    observeRegions();
    refresh();
  });
})();
